const PEAK_BINS = 240;
const cache = new Map<string, Float32Array>();
const inflight = new Map<string, Promise<Float32Array | null>>();

function cacheKey(source: Blob | string): string {
  if (typeof source === 'string') return source;
  return `blob:${source.size}:${source.type}`;
}

async function bufferFromSource(source: Blob | string): Promise<ArrayBuffer> {
  if (typeof source !== 'string') return source.arrayBuffer();
  const resp = await fetch(source);
  if (!resp.ok) throw new Error(`Falha ao carregar áudio (${resp.status})`);
  return resp.arrayBuffer();
}

function peaksFromChannel(channel: Float32Array, bins: number): Float32Array {
  const peaks = new Float32Array(bins);
  const block = Math.max(1, Math.floor(channel.length / bins));
  let loudest = 0;
  for (let i = 0; i < bins; i++) {
    let max = 0;
    const start = i * block;
    const end = Math.min(channel.length, start + block);
    for (let j = start; j < end; j++) {
      const v = Math.abs(channel[j]);
      if (v > max) max = v;
    }
    peaks[i] = max;
    if (max > loudest) loudest = max;
  }
  if (loudest > 0) {
    for (let i = 0; i < bins; i++) peaks[i] /= loudest;
  }
  return peaks;
}

/** Decode blob/URL and return normalized peak bins, or null if there is no audio. */
export async function loadAudioPeaks(source: Blob | string): Promise<Float32Array | null> {
  const key = cacheKey(source);
  const hit = cache.get(key);
  if (hit) return hit;
  const pending = inflight.get(key);
  if (pending) return pending;

  const job = (async () => {
    const ctx = new AudioContext();
    try {
      const raw = await bufferFromSource(source);
      const audio = await ctx.decodeAudioData(raw.slice(0));
      if (!audio.numberOfChannels || audio.length < 32) return null;
      const peaks = peaksFromChannel(audio.getChannelData(0), PEAK_BINS);
      cache.set(key, peaks);
      return peaks;
    } catch {
      return null;
    } finally {
      inflight.delete(key);
      void ctx.close();
    }
  })();

  inflight.set(key, job);
  return job;
}

export function slicePeaks(
  peaks: Float32Array,
  trimStart: number,
  trimEnd: number,
  originalDuration: number,
): Float32Array {
  if (!originalDuration || peaks.length < 2) return peaks;
  const startRatio = Math.min(0.98, Math.max(0, trimStart / originalDuration));
  const endRatio = Math.min(1, Math.max(startRatio + 0.02, 1 - trimEnd / originalDuration));
  const a = Math.floor(startRatio * peaks.length);
  const b = Math.max(a + 1, Math.ceil(endRatio * peaks.length));
  return peaks.subarray(a, b);
}
